import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api";

const sectionSchema = z.object({ title: z.string(), body: z.string() });
export const strategySnapshotSchema = z.object({
  asin: z.literal("B0GG6YTX2H"),
  metrics: z.object({
    units: z.number().int().nonnegative(),
    unitSessionRate: z.number().nonnegative(),
    peerUnitSessionRate: z.number().nonnegative(),
    acos: z.number().nonnegative(),
    peerAcos: z.number().nonnegative(),
    categorySpendShare: z.number().nonnegative(),
  }),
  forecastBasis: z
    .object({
      periodDays: z.number().int().positive(),
      observedFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      observedTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      adSpend: z.number().nonnegative(),
      attributedSales: z.number().nonnegative(),
    })
    .optional(),
  rankBasis: z
    .object({
      category: z.string(),
      currentRank: z.number().int().positive(),
      previousRank: z.number().int().positive(),
      approxObservationGapDays: z.number().int().positive(),
      capturedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      sourceUrl: z.string().url(),
    })
    .optional(),
  keywords: z.array(
    z.object({
      keyword: z.string(),
      clicks: z.number().int().nonnegative(),
      orders: z.number().int().nonnegative(),
      acos: z.number().nonnegative(),
      status: z.string(),
    }),
  ),
  tactics: z.array(
    z.object({ id: z.string(), reason: z.string(), sections: z.array(sectionSchema) }),
  ),
  decision: z.object({
    title: z.string(),
    summary: z.string(),
    rationale: z.string(),
    budget: z.string(),
    safeguards: z.string(),
    proposedGroups: z.array(z.string()),
  }),
});

export type StrategySnapshot = z.infer<typeof strategySnapshotSchema>;

const responseSchema = z.object({ success: z.literal(true), data: strategySnapshotSchema });

export function strategySnapshotQuery(userId: string, authorizedShopIds: string[]) {
  return queryOptions({
    queryKey: ["strategy-snapshot", userId, [...authorizedShopIds].sort()],
    queryFn: async () => {
      const response: unknown = await api.get("strategy-assets/data").json();
      return responseSchema.parse(response).data;
    },
    staleTime: 5 * 60_000,
  });
}
